import { useState, useEffect, useRef } from "react";
import BetterTogetherSectionComponent from "../components/BetterTogetherSection";
import BrandStorySections from "../components/BrandStorySections";
import PromptSuggestions from "../components/PromptSuggestions";
import { sfLogger } from "../services/logger";

import "../styles/VisionForm.css";
import DomainSearchSectionComponent from "../components/DomainSearchSection";

const AI_BRAND_CREATOR_URL = "https://app.agents.networksolutions.com/ai-brand-creator/app";
const CHANNEL_ID = "P99C100S653N0B5A1D0E0000V100";

const PROMPT_PLACEHOLDERS = [
  "Build a professional photography portfolio featuring my landscape work from travels across national parks, with a clean gallery, about page, and contact form.",
  "Create a modern website for a neighborhood bakery that highlights custom cakes, online ordering, catering, and customer reviews.",
  "Design a polished landing page for a local law firm with practice areas, attorney profiles, trust signals, and a consultation form.",
  "Generate a warm, inviting site for a family-owned plumbing business with emergency services, service areas, transparent pricing, and booking.",
  "Build an online coaching website for a personal trainer with programs, transformation stories, pricing, and a lead capture form.",
  "Create a boutique hotel website with room showcases, local attractions, guest testimonials, seasonal offers, and a reservation call to action.",
  "Build a sleek website for a SaaS startup that explains the product, compares plans, captures demo requests, and highlights customer success stories.",
  "Design an online store for eco-friendly home goods with featured collections, sustainability messaging, product reviews, and newsletter signup.",
  "Create a portfolio site for a freelance graphic designer with case studies, services, client logos, and an inquiry form.",
  "Build a restaurant website with menus, online reservations, event catering, chef story, and photos that make the food feel irresistible.",
  "Generate a nonprofit website that explains the mission, shares impact stories, accepts donations, and recruits volunteers.",
  "Create a real estate agent website with featured listings, neighborhood guides, buyer and seller resources, and a contact form.",
  "Build a tutoring service website with subject pages, tutor bios, parent testimonials, pricing, and a simple booking flow.",
  "Design a wellness spa website with treatment descriptions, gift card promotion, membership options, and an easy appointment request.",
  "Create a construction company website with project galleries, service areas, safety credentials, customer reviews, and quote requests.",
];

export default function VisionForm() {
  const [prompt, setPrompt] = useState("");
  const [promptNotice, setPromptNotice] = useState("");
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const introSectionRef = useRef(null);
  const promptTextareaRef = useRef(null);

  function handleSubmit(e) {
    e.preventDefault();

    const url = new URL(AI_BRAND_CREATOR_URL);
    url.searchParams.set("q", prompt);
    url.searchParams.set("channelID", CHANNEL_ID);

    sfLogger.info({
      message: "AI SiteGen prompt submitted",
      eventType: "submitted",
      brand: "NETWORKSOLUTIONS",
      customAttributes: {
        element: "web.com_ai_builder_prompt_form",
        channelID: CHANNEL_ID,
        destinationUrl: url.toString(),
        prompt,
      },
    });

    window.open(url.toString(), "_blank", "noopener,noreferrer");
  }

  useEffect(() => {
    if (window.self !== window.top) {
      // App is being loaded inside the Iframe
      window.top.location.href = window.location.href;
    }
  }, []);

  useEffect(() => {
    const textarea = promptTextareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [prompt]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setPlaceholderIndex((currentIndex) => (currentIndex + 1) % PROMPT_PLACEHOLDERS.length);
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, []);

  function handleSuggestionClick(suggestion) {
    setPrompt(`${suggestion.title}\n\n${suggestion.description}\n\n${suggestion.unique}`);
    setPromptNotice("We've filled in a starter prompt for you. You can edit it before continuing.");

    requestAnimationFrame(() => {
      introSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      promptTextareaRef.current?.focus({ preventScroll: true });
    });
  }

  return (
    <>
      <DomainSearchSectionComponent />
      <section className="vs-intro-section" ref={introSectionRef}>
        <div className="vs-page-container">
          <div className="vs-page-header">
            <p className="vs-eyebrow">AI Website Builder</p>
            <h1 className="vs-page-title">Your brand, your way.</h1>
            <p className="vs-page-subtitle">It&apos;s as easy as web.com</p>
          </div>
          <div className="vs-page-content">
            <div className="vs-right-card">
              <form onSubmit={handleSubmit} className="vs-form">
                <div className="vs-field">
                  <label>Describe the website you want to build</label>
                  <textarea
                    ref={promptTextareaRef}
                    value={prompt}
                    onChange={(e) => {
                      setPrompt(e.target.value);
                      setPromptNotice("");
                    }}
                    required
                    placeholder={PROMPT_PLACEHOLDERS[placeholderIndex]}
                  />
                  {promptNotice && (
                    <p className="vs-prompt-notice" role="status" aria-live="polite">
                      {promptNotice}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  className="vs-cta"
                  data-element-type="cta"
                  data-element-location="body"
                  data-element-label="Build My Website"
                >
                  Build my website →
                </button>
              </form>
            </div>
          </div>
          <div className="vs-trust-row" aria-label="Website builder highlights">
            <span>AI-powered website preview</span>
            <span>Built for small businesses</span>
            <span>No design experience needed</span>
          </div>
        </div>
      </section>
      <PromptSuggestions setSuggestion={handleSuggestionClick} />
      <BrandStorySections />
      <BetterTogetherSectionComponent />
    </>
  );
}
